import { useRef, useCallback, useEffect } from 'react';

/**
 * Waits for sustained mouse hover before opening a menu — and only opening;
 * this hook has no concept of "close" at all (dismissal is handled
 * elsewhere, see useMegaMenuDismiss).
 *
 * How it works: hovering starts a timer for `openDelay` ms. If the mouse
 * leaves before that timer finishes, the pending open is cancelled and
 * `onOpen` never fires. If the mouse leaves after `onOpen` already fired,
 * that's just a no-op — there's nothing left to cancel.
 *
 * Why focus doesn't also trigger `onOpen`: a keyboard user tabbing onto the
 * trigger should NOT have its menu pop open just from receiving focus — that
 * case is handled separately by FeaturedNavBar's own explicit
 * keyboard-activation logic. `onBlur` still cancels a pending mouse-hover
 * timer, though: if the mouse is hovering (timer running) and focus then
 * moves away for any reason, there's no longer a reason to open the menu, so
 * the pending timer is cleared.
 *
 * @param {object} options
 * @param {() => void} options.onOpen - Called when hover intent is confirmed.
 * @param {number} [options.openDelay=150] - Milliseconds before confirming hover intent.
 * @returns {{
 *   onMouseEnter: () => void,
 *   onMouseLeave: () => void,
 *   onBlur: () => void,
 *   cancel: () => void
 * }}
 */
const useHoverOpenIntent = ({ onOpen, openDelay = 150 }) => {
	const openTimerRef = useRef(null);
	const onOpenRef = useRef(onOpen);

	onOpenRef.current = onOpen;

	const cancel = useCallback(() => {
		clearTimeout(openTimerRef.current);
		openTimerRef.current = null;
	}, []);

	const handleEnter = useCallback(() => {
		clearTimeout(openTimerRef.current);
		openTimerRef.current = setTimeout(() => {
			onOpenRef.current();
		}, openDelay);
	}, [openDelay]);

	const handleLeave = useCallback(() => {
		clearTimeout(openTimerRef.current);
		openTimerRef.current = null;
	}, []);

	useEffect(() => {
		return () => {
			clearTimeout(openTimerRef.current);
		};
	}, []);

	return {
		onMouseEnter: handleEnter,
		onMouseLeave: handleLeave,
		onBlur: handleLeave,
		cancel
	};
};

export default useHoverOpenIntent;
